import styled, { keyframes } from 'styled-components';
import Typography from '@klv/atoms/typography';
import Grid from '@klv/utils/grid';
import type { IImageContainer, IMenuButton, ITransientProps } from '../types';

const animations = {
  in: {
    mobile: {
      left: keyframes`
        0% { transform: translateX(100%) }
        100% { transform: translateX(0) }
      `,
      right: keyframes`
        0% { transform: translateX(-100%) }
        100% { transform: translateX(0) }
      `,
    },
    desktop: {
      left: keyframes`
        0% { transform: translateY(100%) }
        100% { transform: translateY(0) }
      `,
      right: keyframes`
        0% { transform: translateY(-100%) }
        100% { transform: translateY(0) }
      `,
    },
  },
  out: {
    mobile: {
      left: keyframes`
        0% { transform: translateX(0) }
        100% { transform: translateX(-100%) }
      `,
      right: keyframes`
        0% { transform: translateX(0) }
        100% { transform: translateX(100%) }
      `,
    },
    desktop: {
      left: keyframes`
        0% { transform: translateY(0) }
        100% { transform: translateY(-100%) }
      `,
      right: keyframes`
        0% { transform: translateY(0) }
        100% { transform: translateY(100%) }
      `,
    },
  },
};

export const ImageContainer = styled.div<IImageContainer>`
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
  animation-name: ${({ $dir, $segment }) => animations[$segment].mobile[$dir]};
  grid-column: 1 / -1;
  grid-row: 2;
  margin-bottom: 2.4rem;
  opacity: ${({ $current }) => ($current ? '1' : '0')};
  pointer-events: none;
  transition: opacity 0.5s ease-in-out;

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    animation-name: ${({ $dir, $segment }) => animations[$segment].desktop[$dir]};
    grid-column: 7 / -1;
    grid-row: 1 / span 2;
    margin-bottom: 0;
    padding-top: 5rem;
  }

  ${({ theme }) => theme.screenSize.s1024.mediaQuery} {
    padding-top: 5rem;
  }

  ${({ theme }) => theme.screenSize.s1200.mediaQuery} {
    padding-top: 0;
  }
`;

export const Menu = styled.div`
  display: flex;
  grid-column: 1 / -1;
  grid-row: 1;
  justify-content: space-evenly;
  margin-bottom: 2.6rem;

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    display: block;
    grid-column: 1 / 6;
    margin-bottom: 7rem;
  }

  ${({ theme }) => theme.screenSize.s1366.mediaQuery} {
    margin-bottom: 9.6rem;
  }
`;

export const MenuButton = styled(Typography)<IMenuButton>`
  background-color: transparent;
  border: 0;
  color: ${({ $current, theme }) => ($current ? theme.twoColumnSlider.buttonHoverColor : theme.twoColumnSlider.buttonColor)};
  cursor: pointer;
  display: inline-block;
  margin-bottom: 1.6rem;
  opacity: ${({ $current }) => ($current ? 1 : 0.65)};
  padding: 0;
  text-transform: uppercase;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.twoColumnSlider.buttonHoverColor};
    opacity: 1;
  }

  &:focus-visible {
    outline: 1px dashed ${({ theme }) => theme.twoColumnSlider.text};
    outline-offset: 2px;
  }

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    display: block;
  }
`;

export const StyledGrid = styled(Grid)`
  grid-template-rows: min-content 1fr;
`;

export const StyledHeadline = styled(Typography)`
  margin-bottom: 1.6rem;

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    margin-bottom: 2.5rem;
  }
`;

export const StyledBodyText = styled(Typography)`
  margin-bottom: 4.8rem;
`;

export const StyledTwoColumnSlider = styled.div`
  background-color: ${({ theme }) => theme.twoColumnSlider.background};
  overflow: hidden;
  ${({ theme }) => theme.globalPadding};
  position: relative;
`;

export const TwoColumnSliderText = styled.div<ITransientProps>`
  grid-column: 1 / -1;
  grid-row: 3;
  opacity: ${({ $current }) => ($current ? 1 : 0)};
  transition-delay: ${({ $current }) => ($current ? '0.25s' : '0')};
  transition-duration: 0.25s;
  transition-property: opacity, visibility;
  transition-timing-function: ease-in-out;
  visibility: ${({ $current }) => ($current ? 'visible' : 'hidden')};

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    grid-column: 1 / 6;
    grid-row: 2;
  }
`;
